import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  List,
  Card,
  Button,
  Space,
  Tag,
  Badge,
  Empty,
  message,
  Select,
  Typography,
  Segmented,
  Tooltip,
} from 'antd'
import {
  BellOutlined,
  CheckOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { notificationsApi } from '@/api/notifications'
import type { Notification } from '@/types/notification'
import { getApiErrorMessage } from '@/utils/error'

dayjs.extend(relativeTime)

const { Title, Text, Paragraph } = Typography

export const NotificationCenter = () => {
  const queryClient = useQueryClient()
  const [filter, setFilter] = useState<'all' | 'unread'>('all')
  const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined)
  const [severityFilter, setSeverityFilter] = useState<string | undefined>(undefined)

  // Query for notifications
  const { data, isLoading } = useQuery({
    queryKey: ['notifications', { filter, typeFilter, severityFilter }],
    queryFn: () =>
      notificationsApi.list({
        unreadOnly: filter === 'unread',
        type: typeFilter,
        severity: severityFilter,
        pageSize: 50,
      }),
    refetchInterval: 60000, // Auto-refresh every minute
  })

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: (id: string) => notificationsApi.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to mark notification as read'))
    },
  })

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: () => notificationsApi.markAllAsRead(),
    onSuccess: (data) => {
      message.success(`Marked ${data.data.count} notifications as read`)
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to mark all notifications as read'))
    },
  })

  const getSeverityIcon = (severity: Notification['severity']) => {
    const config = {
      info: { icon: <InfoCircleOutlined />, color: '#1890ff' },
      warning: { icon: <WarningOutlined />, color: '#faad14' },
      critical: { icon: <CloseCircleOutlined />, color: '#ff4d4f' },
    }
    return config[severity]
  }

  const getSeverityTag = (severity: Notification['severity']) => {
    const config = {
      info: { color: 'blue', text: 'Info' },
      warning: { color: 'gold', text: 'Warning' },
      critical: { color: 'red', text: 'Critical' },
    }
    const { color, text } = config[severity]
    return <Tag color={color}>{text}</Tag>
  }

  const getTypeTag = (type: Notification['type']) => {
    const config = {
      approval_request: { color: 'purple', text: 'Approval Request' },
      permission_granted: { color: 'green', text: 'Permission Granted' },
      permission_revoked: { color: 'orange', text: 'Permission Revoked' },
      system: { color: 'default', text: 'System' },
    }
    const { color, text } = config[type]
    return <Tag color={color}>{text}</Tag>
  }

  const notifications = data?.data?.notifications || []
  const unreadCount = data?.data?.unreadCount || 0

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space>
                <BellOutlined style={{ fontSize: '24px' }} />
                <Title level={3} style={{ margin: 0 }}>
                  Notifications
                </Title>
                {unreadCount > 0 && (
                  <Badge count={unreadCount} style={{ backgroundColor: '#52c41a' }} />
                )}
              </Space>
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={() => markAllAsReadMutation.mutate()}
                loading={markAllAsReadMutation.isPending}
                disabled={unreadCount === 0}
              >
                Mark All as Read
              </Button>
            </div>

            <Space wrap>
              <Segmented
                value={filter}
                onChange={(value) => setFilter(value as 'all' | 'unread')}
                options={[
                  { label: 'All', value: 'all' },
                  { label: `Unread (${unreadCount})`, value: 'unread' },
                ]}
              />

              <Select
                style={{ width: 180 }}
                placeholder="Filter by type"
                value={typeFilter}
                onChange={setTypeFilter}
                allowClear
                options={[
                  { label: 'Approval Request', value: 'approval_request' },
                  { label: 'Permission Granted', value: 'permission_granted' },
                  { label: 'Permission Revoked', value: 'permission_revoked' },
                  { label: 'System', value: 'system' },
                ]}
              />

              <Select
                style={{ width: 150 }}
                placeholder="Filter by severity"
                value={severityFilter}
                onChange={setSeverityFilter}
                allowClear
                options={[
                  { label: 'Info', value: 'info' },
                  { label: 'Warning', value: 'warning' },
                  { label: 'Critical', value: 'critical' },
                ]}
              />
            </Space>
          </Space>
        </Card>

        <Card bodyStyle={{ padding: 0 }}>
          {notifications.length === 0 ? (
            <Empty description="No notifications" style={{ padding: '48px 0' }} />
          ) : (
            <List
              itemLayout="horizontal"
              dataSource={notifications}
              loading={isLoading}
              pagination={{
                pageSize: 20,
                showSizeChanger: false,
                showTotal: (total) => `Total ${total} notifications`,
              }}
              renderItem={(notification) => {
                const severityConfig = getSeverityIcon(notification.severity)
                const isUnread = !notification.read_at

                return (
                  <List.Item
                    style={{
                      padding: '16px 24px',
                      backgroundColor: isUnread ? '#f0f5ff' : 'transparent',
                      borderLeft: isUnread ? '4px solid #1890ff' : '4px solid transparent',
                    }}
                    actions={[
                      isUnread && (
                        <Tooltip title="Mark as read">
                          <Button
                            type="text"
                            icon={<CheckOutlined />}
                            onClick={() => markAsReadMutation.mutate(notification.id)}
                            loading={markAsReadMutation.isPending}
                          />
                        </Tooltip>
                      ),
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {dayjs(notification.created_at).fromNow()}
                      </Text>,
                    ].filter(Boolean)}
                  >
                    <List.Item.Meta
                      avatar={
                        <div style={{ fontSize: '24px', color: severityConfig.color }}>
                          {severityConfig.icon}
                        </div>
                      }
                      title={
                        <Space>
                          {isUnread && <Badge status="processing" />}
                          <Text strong={isUnread}>{notification.title}</Text>
                          {getTypeTag(notification.type)}
                          {getSeverityTag(notification.severity)}
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <Paragraph style={{ margin: 0 }}>{notification.message}</Paragraph>
                          {notification.data && (
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {notification.read_at &&
                                `Read ${dayjs(notification.read_at).fromNow()}`}
                            </Text>
                          )}
                        </Space>
                      }
                    />
                  </List.Item>
                )
              }}
            />
          )}
        </Card>
      </Space>
    </div>
  )
}
